import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import PageHeaderBlock from '@/registry/evo-shadcn/blocks/page-header';
import { SensitiveData } from '@/components/sensitive-data';
import {
  fetchProjects,
  fetchPortfolioSummary,
  fetchClients,
} from '@/lib/data-service';
import {
  formatCurrency,
  formatPercent,
  getHealthBadgeColor,
} from '@/lib/utils';
import type { Project, PortfolioSummary, Client } from '@/types';
import { Briefcase, TrendingUp, DollarSign, AlertTriangle } from 'lucide-react';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { useCompany } from '@/contexts/CompanyContext';

export function PortfolioPage() {
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [portfolio, setPortfolio] = useState<PortfolioSummary | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const { handleApiError } = useErrorHandler({ useToast: true });
  const { currentCompany } = useCompany();

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [projectsResult, portfolioData, clientsData] = await Promise.all([
          fetchProjects('portfolio', { page: 1, pageSize: 50 }, { statusId: 2, sortBy: 'health' }),
          fetchPortfolioSummary('portfolio'),
          fetchClients('portfolio'),
        ]);
        const projectsData = Array.isArray(projectsResult) ? projectsResult : (projectsResult?.data ?? []);
        setProjects(projectsData);
        setPortfolio(portfolioData);
        setClients(clientsData);
      } catch (error) {
        handleApiError(error, '/api/portfolio', 'GET');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [handleApiError, currentCompany?.id]);

  const activeProjects= projects.filter(p => p.statusId === 2);

  return (
    <div className="space-y-6">
      <PageHeaderBlock
        title="Portfolio Overview"
        subtitle="Executive view across all projects"
        size={20}
        showActions={false}
      />

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Projects</CardTitle>
            <Briefcase className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{portfolio?.totalProjects || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {portfolio?.activeProjects || 0} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Portfolio Value</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              <SensitiveData>
                {formatCurrency(portfolio?.portfolioValue || 0)}
              </SensitiveData>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Total contract value
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Avg Margin</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              <SensitiveData>
                {formatPercent(portfolio?.avgMarginPercent || 0)}
              </SensitiveData>
            </div>
            <p className="text-xs text-success-600 mt-1">
              Above target
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total WIP</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              <SensitiveData>
                {formatCurrency(portfolio?.totalWIP || 0)}
              </SensitiveData>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Unbilled work
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Health Heat Map */}
      <Card>
        <CardHeader>
          <CardTitle>Project Health Heat Map</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {activeProjects.map(project => {
              // Get client name - use clientNames if available, otherwise look up first client
              const clientName = project.clientNames || (project.clientIds?.split(',')[0] ? clients.find(c => c.id === Number(project.clientIds?.split(',')[0]))?.name : undefined);
              return (
                <Card
                  key={project.id}
                  className={`cursor-pointer hover:shadow-md transition-shadow ${
                    project.health === 'red'
                      ? 'border-error-300 bg-error-50 dark:bg-error-950/20'
                      : project.health === 'amber'
                      ? 'border-warning-300 bg-warning-50 dark:bg-warning-950/20'
                      : 'border-success-300 bg-success-50 dark:bg-success-950/20'
                  }`}
                >
                  <CardContent className="p-4">
                    <div className="space-y-2">
                      <Badge className={getHealthBadgeColor(project.health)}>
                        {project.health.toUpperCase()}
                      </Badge>
                      <h4 className="font-semibold text-sm line-clamp-2">{project.name}</h4>
                      <p className="text-xs text-muted-foreground">{clientName}</p>
                      <div className="text-xs">
                        <SensitiveData>
                          {formatCurrency(project.budgetRevenue)}
                        </SensitiveData>
                      </div>
                      <div className="text-xs font-medium">
                        {formatPercent(project.completionPercent, 0)} complete
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Status Breakdown */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Projects by Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {portfolio && Object.entries(portfolio.projectsByStatus).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <span className="text-sm capitalize">{status.replace('-', ' ')}</span>
                  <Badge variant="outline">{count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Projects by Health</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {portfolio && Object.entries(portfolio.projectsByHealth).map(([health, count]) => (
                <div key={health} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className={`h-3 w-3 rounded-full ${
                        health === 'green'
                          ? 'bg-success-600'
                          : health === 'amber'
                          ? 'bg-warning-600'
                          : 'bg-error-600'
                      }`}
                    />
                    <span className="text-sm capitalize">{health}</span>
                  </div>
                  <Badge variant="outline">{count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
